import { resetLocalAuthKey, setLocalAuthKey } from '@/common/auth';
import { includesError } from '@/common/errors';
import { ContentPanel } from '@/components/content-panel';
import { useCurrentUser } from '@/hooks';
import { IconUser } from '@tabler/icons-react';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { CompactButton } from '../../components/buttons/compact';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { LoginForm, LoginFormValues } from './form';
import { useLoginPasswordMutation } from './graphql.generated';
import { useMigratedUserIsLoggingInForTheFirstTime } from './use-first-time-migrated-user/hook';

export function Login() {
	const navigate = useNavigate();
	const router = useRouter();
	const search = useSearch({ from: '/auth/login' });
	const { refetchUser } = useCurrentUser({ skip: true });
	const [loginPassword] = useLoginPasswordMutation({ errorPolicy: 'all' });
	const { checkMigratedUserIsLoggingInForTheFirstTime } =
		useMigratedUserIsLoggingInForTheFirstTime();

	const signupButton = useMemo(
		() => (
			<CompactButton leftSection={<IconUser />} onClick={() => navigate({ to: '/signup' })}>
				Sign Up
			</CompactButton>
		),
		[navigate]
	);
	const formRef = useRef<FormikProps<LoginFormValues>>(null);
	const onSubmit = useCallback(() => formRef.current?.submitForm(), []);
	const loginButton = useMemo(
		() => (
			<StandardButton type="submit" variant="filled" onClick={onSubmit}>
				Log In
			</StandardButton>
		),
		[onSubmit]
	);

	const handleSubmit = useCallback(
		async (values: LoginFormValues) => {
			try {
				resetLocalAuthKey(); // TODO This is a client side solution for what should really be solved on the server
				const { data, errors } = await loginPassword({
					variables: {
						username: values.email.toLowerCase(),
						password: values.password,
					},
				});
				if (data?.loginPassword?.authToken) {
					setLocalAuthKey(data.loginPassword.authToken);
					await refetchUser();
					const destination = (await checkMigratedUserIsLoggingInForTheFirstTime())
						? '/inform-existing'
						: (search.redirect ?? '/home');
					router.history.push(destination);
				}
				if (includesError(errors, 'Bad Request: Authentication Failed')) {
					formRef.current?.setFieldError('password', 'Username or password is incorrect');
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
			}
		},
		[
			loginPassword,
			router.history,
			search.redirect,
			checkMigratedUserIsLoggingInForTheFirstTime,
			refetchUser,
		]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Log In', backTo: '/welcome', children: signupButton }}
			footer={{ children: loginButton }}
		>
			<ContentPanel justify="center" flex={1}>
				<LoginForm onSubmit={handleSubmit} formRef={formRef} />
			</ContentPanel>
		</PrimaryLayout>
	);
}
